import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { AdminDeckDto } from '@lunary/api-client'
import { ConfirmDialog } from '../../shared/ui/confirm-dialog'
import { BackPanel } from './back-panel'
import { DeckPopover } from './deck-popover'
import { deckErrorText } from './errors'
import { KIND_TITLE } from './kinds'
import { useDeleteDeck, useUpdateDeck } from './queries'
import styles from './decks.module.css'

type Props = {
  deck: AdminDeckDto
  uploadsEnabled: boolean
  canSetDefault: boolean
  onDeleted: () => void
}

function Toggle({ label, on, gold, disabled, onClick }: { label: string; on: boolean; gold?: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} className={`${styles.toggle} ${gold ? styles.toggleGold : ''}`} disabled={disabled} onClick={onClick}>
      <i aria-hidden="true" />
      {label}
    </button>
  )
}

export function DeckToolbar({ deck, uploadsEnabled, canSetDefault, onDeleted }: Props) {
  const update = useUpdateDeck(deck.id)
  const remove = useDeleteDeck(deck.id)
  const [naming, setNaming] = useState<string | null>(null)
  const [describing, setDescribing] = useState<string | null>(null)
  const [confirm, setConfirm] = useState(false)
  const daily = deck.kind === 'daily_card'
  const missing = deck.required_count - (daily ? deck.majors_count : deck.cards_count)
  const target = daily ? '«Карты дня»' : '«Трёх карт»'
  const count = daily ? `${deck.majors_count} из 22 · свои ${deck.custom_cards.length}` : `${deck.cards_count} из 78`
  const hint = deck.is_default
    ? `Пользователи видят её в ${daily ? '«Карте дня», орбите и разборах' : '«Трёх картах»'}.`
    : !deck.enabled && missing > 0
      ? `Чтобы включить, добавьте ещё ${missing} ${daily ? 'старших арканов' : 'карт'}.`
      : `Основная колода ${target} одна на приложение, её назначают админ и Людмила.`
  const name = naming?.trim() ?? ''

  return (
    <header className={styles.toolbarWrap}>
      <nav className={styles.crumb} aria-label="Раздел">
        <Link to="/decks" className={styles.allDecks}>
          ← Все колоды
        </Link>
        <span aria-hidden="true">/</span>
        <span>{KIND_TITLE[deck.kind]}</span>
      </nav>
      <div className={styles.toolbar}>
        <DeckPopover
          label="Рубашка колоды"
          triggerClassName={styles.backTrigger}
          trigger={
            <>
              {deck.back ? <img src={deck.back.thumb_url} alt="" draggable={false} /> : <span className={styles.backStd} />}
              <span aria-hidden="true">▾</span>
            </>
          }
        >
          <BackPanel deck={deck} uploadsEnabled={uploadsEnabled} />
        </DeckPopover>
        {naming === null ? (
          <h1 className={styles.title}>
            {deck.name}
            <button type="button" className={styles.pen} aria-label="Переименовать колоду" onClick={() => setNaming(deck.name)}>
              ✎
            </button>
          </h1>
        ) : (
          <form
            className={styles.rename}
            onSubmit={(e) => {
              e.preventDefault()
              if (name) update.mutate({ name }, { onSuccess: () => setNaming(null) })
            }}
          >
            <input
              aria-label="Название колоды"
              value={naming}
              maxLength={80}
              autoFocus
              onChange={(e) => setNaming(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setNaming(null)
              }}
            />
            <button type="submit" disabled={!name || update.isPending}>
              Сохранить
            </button>
            <button type="button" className="secondary" onClick={() => setNaming(null)}>
              Отмена
            </button>
          </form>
        )}
        <span className={styles.sep} aria-hidden="true" />
        <Toggle label="Включена" on={deck.enabled} disabled={update.isPending || (!deck.enabled && missing > 0)} onClick={() => update.mutate({ enabled: !deck.enabled })} />
        {canSetDefault && <Toggle label="Основная" gold on={deck.is_default} disabled={update.isPending || !deck.enabled} onClick={() => update.mutate({ is_default: !deck.is_default })} />}
        <span className={styles.grow} aria-hidden="true" />
        <DeckPopover label="Действия с колодой" menu align="end" triggerClassName={`secondary ${styles.more}`} trigger="⋯">
          <>
            <button type="button" role="menuitem" onClick={() => setNaming(deck.name)}>
              Переименовать
            </button>
            <button type="button" role="menuitem" onClick={() => setDescribing(deck.description)}>
              Описание для пользователя
            </button>
            <button type="button" role="menuitem" className={styles.menuDanger} onClick={() => setConfirm(true)}>
              Удалить колоду
            </button>
          </>
        </DeckPopover>
      </div>
      {describing === null ? (
        <p className={styles.toolbarSub}>
          {deck.description && <span>{deck.description}</span>}
          <span>{count}</span>
          <span>автор {deck.owner_name ?? '—'}</span>
          <span>{hint}</span>
        </p>
      ) : (
        <form
          className={styles.describe}
          onSubmit={(e) => {
            e.preventDefault()
            update.mutate({ description: describing }, { onSuccess: () => setDescribing(null) })
          }}
        >
          <label htmlFor="deck-desc" className={styles.field}>
            Описание для пользователя
            <input id="deck-desc" value={describing} maxLength={300} autoFocus onChange={(e) => setDescribing(e.target.value)} />
          </label>
          <div className={styles.actions}>
            <button type="submit" disabled={describing === deck.description || update.isPending}>
              Сохранить
            </button>
            <button type="button" className="secondary" onClick={() => setDescribing(null)}>
              Отмена
            </button>
          </div>
        </form>
      )}
      {update.isError && <p className={styles.error}>{deckErrorText(update.error)}</p>}
      <ConfirmDialog
        open={confirm}
        title="Удалить колоду?"
        text={`Колода «${deck.name}» и привязка картинок пропадут. Если её свои карты уже были картой дня, удалить не получится: выключите колоду.`}
        confirmLabel="Удалить"
        danger
        pending={remove.isPending}
        error={remove.isError ? deckErrorText(remove.error) : null}
        onClose={() => setConfirm(false)}
        onConfirm={() => remove.mutate(undefined, { onSuccess: onDeleted })}
      />
    </header>
  )
}
